import React, { createContext, useContext, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { DEFAULT_DICT } from "@/contexts/I18nContext";
import { getPath, setPath, pruneEmpty } from "@/lib/textOverrides";

const EditCtx = createContext(null);
const TOKEN_KEY = "anypaku.admin.token";

/**
 * Edit mode is available only when an admin token is present in localStorage.
 * The user toggles it on with the floating "✏️ Mode édition" button.
 * When on, wrapping <Edit path="..."/> components show a pencil overlay
 * and clicking opens a side panel to edit the FI + EN value.
 */
export const EditModeProvider = ({ children }) => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [editing, setEditing] = useState(false);
  const [activePath, setActivePath] = useState(null);
  const [overrides, setOverrides] = useState({ fi: {}, en: {} });

  // Detect admin token + listen for changes
  useEffect(() => {
    const refresh = () => setIsAdmin(!!localStorage.getItem(TOKEN_KEY));
    refresh();
    window.addEventListener("storage", refresh);
    window.addEventListener("anypaku-auth-changed", refresh);
    return () => {
      window.removeEventListener("storage", refresh);
      window.removeEventListener("anypaku-auth-changed", refresh);
    };
  }, []);

  // Load current overrides (so the side panel shows the current value)
  const reload = async () => {
    try {
      const data = await api.getTexts();
      setOverrides({ fi: data?.fi || {}, en: data?.en || {} });
    } catch {
      /* keep stale */
    }
  };

  useEffect(() => {
    reload();
    const handler = () => reload();
    window.addEventListener("anypaku-texts-changed", handler);
    return () => window.removeEventListener("anypaku-texts-changed", handler);
  }, []);

  const saveField = async (path, fiVal, enVal) => {
    const nextFi = setPath(overrides.fi, path, fiVal || "");
    const nextEn = setPath(overrides.en, path, enVal || "");
    const payload = {
      fi: pruneEmpty(nextFi) || {},
      en: pruneEmpty(nextEn) || {},
    };
    await api.putTexts(payload);
    setOverrides({ fi: payload.fi, en: payload.en });
    window.dispatchEvent(new CustomEvent("anypaku-texts-changed"));
  };

  return (
    <EditCtx.Provider
      value={{
        isAdmin,
        editing,
        setEditing,
        activePath,
        setActivePath,
        overrides,
        saveField,
      }}
    >
      {children}
      {isAdmin && <EditModeToggle />}
      {isAdmin && editing && <EditPanel />}
    </EditCtx.Provider>
  );
};

export const useEditMode = () => useContext(EditCtx);

/* -------- Floating toggle button -------- */
const EditModeToggle = () => {
  const { editing, setEditing, setActivePath } = useEditMode();
  return (
    <button
      type="button"
      onClick={() => {
        setEditing((v) => !v);
        if (editing) setActivePath(null);
      }}
      className="fixed z-[60] rounded-full font-bold shadow-2xl transition-all"
      style={{
        bottom: "calc(env(safe-area-inset-bottom, 0px) + 96px)",
        left: 16,
        padding: "12px 18px",
        background: editing ? "var(--orange)" : "var(--navy)",
        color: "#FFFFFF",
        fontSize: 13,
        letterSpacing: "0.04em",
      }}
      data-testid="edit-mode-toggle"
    >
      {editing ? "✓ Mode édition actif" : "✏️ Mode édition"}
    </button>
  );
};

/* -------- Side panel for editing a single path -------- */
const EditPanel = () => {
  const { activePath, setActivePath, overrides, saveField } = useEditMode();
  const [fiVal, setFiVal] = useState("");
  const [enVal, setEnVal] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [hint, setHint] = useState("");

  useEffect(() => {
    if (!activePath) return;
    const cur = {
      fi: getPath(overrides.fi, activePath) ?? "",
      en: getPath(overrides.en, activePath) ?? "",
    };
    setFiVal(cur.fi);
    setEnVal(cur.en);
    setError("");
    setHint("");
  }, [activePath, overrides]);

  if (!activePath) return null;

  const defaultFi = getPath(DEFAULT_DICT.fi, activePath) ?? "";
  const defaultEn = getPath(DEFAULT_DICT.en, activePath) ?? "";
  // Multi-line if the default contains a newline OR is long
  const multiline =
    (defaultFi && (defaultFi.length > 60 || defaultFi.includes("\n"))) ||
    (defaultEn && (defaultEn.length > 60 || defaultEn.includes("\n")));
  const Input = multiline ? "textarea" : "input";

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      await saveField(activePath, fiVal, enVal);
      setHint("✓ Enregistré — visible immédiatement");
      setTimeout(() => setHint(""), 2000);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setFiVal("");
    setEnVal("");
    setSaving(true);
    try {
      await saveField(activePath, "", "");
      setHint("↺ Restauré au texte par défaut");
      setTimeout(() => setHint(""), 2000);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {/* Dim background — click to close */}
      <div
        onClick={() => setActivePath(null)}
        className="fixed inset-0 z-[70]"
        style={{ background: "rgba(15, 36, 64, 0.18)" }}
      />
      <aside
        className="fixed z-[71] shadow-2xl flex flex-col"
        style={{
          top: 0,
          right: 0,
          bottom: 0,
          width: "min(440px, 100vw)",
          background: "#FFFFFF",
          borderLeft: "1px solid var(--line)",
        }}
        data-testid="edit-panel"
      >
        <header
          className="p-5 flex items-start justify-between"
          style={{ borderBottom: "1px solid var(--line)" }}
        >
          <div>
            <div
              className="text-xs font-bold"
              style={{
                color: "var(--orange)",
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                fontFamily: "var(--font-mono)",
              }}
            >
              Édition de texte
            </div>
            <div
              className="text-xs mt-1 font-mono"
              style={{ color: "var(--mute)" }}
            >
              {activePath}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setActivePath(null)}
            className="text-xl leading-none px-2"
            style={{ color: "var(--mute)" }}
            data-testid="edit-panel-close"
            aria-label="Fermer"
          >
            ×
          </button>
        </header>

        <div className="p-5 space-y-4 overflow-y-auto flex-1">
          <div>
            <div
              className="text-[10px] mb-1"
              style={{
                color: "var(--mute)",
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                fontFamily: "var(--font-mono)",
              }}
            >
              🇫🇮 Finnois
            </div>
            <Input
              {...(multiline ? { rows: 4 } : {})}
              className="w-full px-3 py-2 rounded-xl border outline-none text-[14px]"
              style={{ borderColor: "#DCE3ED", color: "var(--navy)" }}
              value={fiVal}
              placeholder={defaultFi}
              onChange={(e) => setFiVal(e.target.value)}
              data-testid="edit-panel-fi"
              autoFocus
            />
          </div>
          <div>
            <div
              className="text-[10px] mb-1"
              style={{
                color: "var(--mute)",
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                fontFamily: "var(--font-mono)",
              }}
            >
              🇬🇧 Anglais
            </div>
            <Input
              {...(multiline ? { rows: 4 } : {})}
              className="w-full px-3 py-2 rounded-xl border outline-none text-[14px]"
              style={{ borderColor: "#DCE3ED", color: "var(--navy)" }}
              value={enVal}
              placeholder={defaultEn}
              onChange={(e) => setEnVal(e.target.value)}
              data-testid="edit-panel-en"
            />
          </div>
          <div className="text-[11px]" style={{ color: "var(--mute)" }}>
            Astuce : laisse vide pour utiliser le texte par défaut.
          </div>
          {hint && (
            <div
              className="text-sm px-3 py-2 rounded-xl"
              style={{ background: "#DDF6E5", color: "#1A6F3A" }}
            >
              {hint}
            </div>
          )}
          {error && (
            <div
              className="text-sm px-3 py-2 rounded-xl"
              style={{ background: "#FFE9EA", color: "#B3263F" }}
            >
              ⚠️ {error}
            </div>
          )}
        </div>

        <footer
          className="p-5 flex flex-col sm:flex-row gap-2"
          style={{ borderTop: "1px solid var(--line)" }}
        >
          <button
            type="button"
            onClick={handleReset}
            disabled={saving}
            className="btn-ghost"
            style={{ padding: "10px 16px", fontSize: 13 }}
            data-testid="edit-panel-reset"
          >
            ↺ Texte par défaut
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="btn-primary flex-1"
            style={{ padding: "10px 16px", fontSize: 14 }}
            data-testid="edit-panel-save"
          >
            {saving ? "Enregistrement…" : "💾 Enregistrer"}
          </button>
        </footer>
      </aside>
    </>
  );
};

/* -------- <Edit path="..." /> wrapper -------- */
/**
 * Renders children normally. When edit mode is on, adds a dashed outline +
 * pencil icon. Click anywhere on the text → opens the EditPanel for `path`.
 *
 * Use <Edit path="hero.title1" inline>...</Edit> for inline (span) wrap.
 */
export const Edit = ({ path, children, inline = false, block = false }) => {
  const ctx = useEditMode();
  if (!ctx?.editing || !ctx?.isAdmin) {
    return inline ? <>{children}</> : <>{children}</>;
  }
  const Cmp = block ? "div" : "span";
  return (
    <Cmp
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        ctx.setActivePath(path);
      }}
      role="button"
      tabIndex={0}
      data-edit-path={path}
      className="anypaku-edit"
      style={{
        position: "relative",
        cursor: "text",
        outline: "2px dashed rgba(245,130,32,0.6)",
        outlineOffset: 3,
        borderRadius: 4,
        display: block ? "block" : "inline-block",
        background: "rgba(245,130,32,0.06)",
        transition: "background 120ms ease",
      }}
    >
      {children}
    </Cmp>
  );
};

export default EditModeProvider;
